import { useState, useEffect, useCallback } from 'react';
import { messagesService } from '../services/firebaseService';
import { Message, Conversation, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../services/firebase';

export interface MessageHookReturn {
  // État des conversations
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Message[];

  // État de l'interface
  loading: boolean;
  error: string | null;
  searchResults: User[];

  // Statistiques
  unreadCount: number;
  onlineUsers: { [userId: string]: boolean };

  // Actions
  sendMessage: (receiverId: string, content: string, type?: 'text' | 'image' | 'gif' | 'nft' | 'token', metadata?: any) => Promise<void>;
  selectConversation: (conversationId: string) => void;
  markAsRead: (otherUserId: string) => Promise<void>;
  searchUsers: (searchTerm: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  startNewConversation: (userId: string) => void;
}

export const useMessages = (): MessageHookReturn => {
  const { user } = useAuth();

  // États
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState<{ [userId: string]: boolean }>({});

  // Charger les conversations
  useEffect(() => {
    if (!user?.id || !auth.currentUser) return;

    setLoading(true);

    // Utiliser l'ID du document utilisateur (user.id) au lieu de l'UID Firebase
    const unsubscribe = messagesService.getConversations(user.id, (updatedConversations: Conversation[]) => {
      setConversations(updatedConversations);

      // Calculer le nombre total de messages non lus
      const totalUnread = updatedConversations.reduce((total: number, conv: Conversation) => {
        return total + (conv.unreadCount || 0);
      }, 0);
      setUnreadCount(totalUnread);

      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Charger les messages de la conversation sélectionnée
  useEffect(() => {
    if (!user?.id || !selectedConversation || !auth.currentUser) {
      setMessages([]);
      return;
    }

    // Trouver l'autre participant (utiliser user.id)
    const otherParticipant = selectedConversation.participants.find((p: string) => p !== user?.id);
    if (!otherParticipant) return;

    const unsubscribe = messagesService.getMessages(user.id, otherParticipant, (updatedMessages: Message[]) => {
      setMessages(updatedMessages);
    });

    return () => unsubscribe();
  }, [user?.id, selectedConversation]);

  // Suivre le statut en ligne des participants
  useEffect(() => {
    if (conversations.length === 0 || !user?.id) return;
    const participantIds = Array.from(new Set(
      conversations.flatMap(conv => conv.participants).filter(id => id !== user.id)
    ));

    if (participantIds.length === 0) return;

    const unsubscribe = messagesService.getUsersOnlineStatus(participantIds, (statuses: { [userId: string]: boolean }) => {
      setOnlineUsers(statuses);
    });

    return () => unsubscribe();
  }, [conversations, user?.id]);

  // Actions
  const sendMessage = useCallback(async (
    receiverId: string, 
    content: string, 
    type: 'text' | 'image' | 'gif' | 'nft' | 'token' = 'text',
    metadata?: any
  ) => {
    if (!user?.id || !content.trim() || !auth.currentUser) return;

    try {
      setError(null);
      // Utiliser l'ID utilisateur Firestore pour la conversation ET comme senderId
      const conversationId = [user.id, receiverId].sort().join('_');
      await messagesService.sendMessage(conversationId, user.id, content.trim(), type, metadata);
    } catch (err) {
      setError('Erreur lors de l\'envoi du message');
      console.error('Erreur envoi message:', err);
    }
  }, [user?.id]);

  const selectConversation = useCallback((conversationId: string) => {
    // Si conversationId est vide ou null, désélectionner la conversation
    if (!conversationId || conversationId === '') {
      setSelectedConversation(null);
      return;
    }

    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setSelectedConversation(conversation);

      // Marquer automatiquement comme lu si l'utilisateur ouvre la conversation
      const otherParticipant = conversation.participants.find((p: string) => p !== user?.id);
      if (otherParticipant && conversation.unreadCount > 0) {
        markAsRead(otherParticipant);
      }
    }
  }, [conversations, user?.id]);

  const markAsRead = useCallback(async (otherUserId: string) => {
    if (!user?.id) return;

    try {
      await messagesService.markMessagesAsRead(user.id, otherUserId);
    } catch (err) {
      console.error('Erreur marquage lu:', err);
    }
  }, [user?.id]);

  const searchUsers = useCallback(async (searchTerm: string) => {
    if (!user?.id || !searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await messagesService.searchUsers(searchTerm.trim(), user.id);
      setSearchResults(results);
    } catch (err) {
      console.error('Erreur recherche utilisateurs:', err);
      setSearchResults([]);
    }
  }, [user?.id]);

  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      await messagesService.deleteMessage(messageId);
    } catch (err) {
      setError('Erreur lors de la suppression du message');
      console.error('Erreur suppression message:', err);
    }
  }, []);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      await messagesService.deleteConversation(conversationId);
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression de la conversation');
      console.error('Erreur suppression conversation:', err);
    }
  }, [selectedConversation]);

  const startNewConversation = useCallback((userId: string) => {
    // Vérifier si une conversation existe déjà
    const existingConversation = conversations.find(conv =>
      conv.participants.includes(userId) && conv.participants.includes(user?.id || '')
    );

    if (existingConversation) {
      setSelectedConversation(existingConversation);
    } else {
      // Créer une pseudo-conversation pour permettre l'envoi du premier message
      const newConversation: Conversation = {
        id: `new_${userId}`,
        participants: [user?.id || '', userId],
        unreadCount: 0,
        createdAt: new Date() as any,
        updatedAt: new Date() as any
      };
      setSelectedConversation(newConversation);
    }
    
    // Effacer les résultats de recherche
    setSearchResults([]);
  }, [conversations, user?.id]);

  return {
    // État des conversations
    conversations,
    selectedConversation,
    messages,

    // État de l'interface
    loading,
    error,
    searchResults,

    // Statistiques
    unreadCount,
    onlineUsers,

    // Actions
    sendMessage,
    selectConversation,
    markAsRead,
    searchUsers,
    deleteMessage,
    deleteConversation,
    startNewConversation
  };
};

// Hook pour les statistiques de messages (optionnel)
export const useMessageStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ totalMessages: 0, unreadCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const loadStats = async () => {
      try {
        const messageStats = await messagesService.getMessageStats(user.id);
        setStats(messageStats);
      } catch (error) {
        console.error('Erreur chargement stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user?.id]);

  return { stats, loading };
};
